import { ExternalLink, ShieldAlert } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { RequirementEligibility } from "@api/services/job-post/pure-requirements-evaluator";

/**
 * Card-level eligibility hint. Renders only when the server reports unmet
 * `block`-severity requirements. Click reveals a popover with the specific
 * unmet requirements and a profile link.
 *
 * Purely presentational: the evaluation arrives already embedded on the job
 * post response, so this issues no request and runs no requirement logic.
 * Server-side enforcement on `setDecision` remains the authoritative gate;
 * this is a UX preview so users see the block on the card instead of finding
 * out after they try to apply.
 */
export function EligibilityBadge({
  eligibility,
}: {
  eligibility: RequirementEligibility | undefined;
}) {
  if (!eligibility?.blocked) {
    return null;
  }

  const count = eligibility.failed.length;
  const label =
    count === 1 ? "Requirement not met" : `${count} requirements not met`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {/* Stop propagation so clicking the badge doesn't trigger any
            card-level interactions (e.g. interest toggles). */}
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          aria-label={label}
        >
          <Badge
            variant="outline"
            className="bg-amber-50 border border-amber-200 text-amber-800 px-2.5 py-1 rounded-md text-sm font-medium gap-1.5 cursor-pointer hover:bg-amber-100"
          >
            <ShieldAlert className="h-4 w-4" aria-hidden />
            {label}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 text-amber-900">
          <ShieldAlert className="h-4 w-4 text-amber-600" aria-hidden />
          <p className="text-sm font-medium">
            You don&apos;t meet this role&apos;s requirements
          </p>
        </div>
        <ul className="space-y-1.5">
          {eligibility.failed.map((req) => (
            <li
              key={req.id}
              className="text-sm text-gray-700 flex items-start gap-2"
            >
              <span
                aria-hidden
                className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0"
              />
              <span>{req.displayLabel}</span>
            </li>
          ))}
        </ul>
        <Link
          to="/provider/profile"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-900 underline hover:text-amber-700"
        >
          Go to My Profile
          <ExternalLink size={12} />
        </Link>
      </PopoverContent>
    </Popover>
  );
}
